import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { GoogleLogin } from '@react-oauth/google';
import { FileText, ArrowLeft } from '@phosphor-icons/react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, setUser } = useAuth();
  const navigate = useNavigate();
  const [googleButtonWidth, setGoogleButtonWidth] = useState(320);

  useEffect(() => {
    const updateGoogleButtonWidth = () => {
      setGoogleButtonWidth(Math.min(400, Math.max(200, Math.floor(window.innerWidth - 48))));
    };

    updateGoogleButtonWidth();
    window.addEventListener('resize', updateGoogleButtonWidth);
    return () => window.removeEventListener('resize', updateGoogleButtonWidth);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const { data } = await axios.post(
        `${API}/auth/google`,
        { credential: credentialResponse.credential },
        { withCredentials: true }
      );
      setUser(data);
      navigate('/dashboard');
    } catch (error) {
      console.error('Google login error:', error);
      setError('Google sign-in failed. Please try again.');
    }
  };

  const handleGoogleError = () => {
    setError('Google sign-in failed. Please try again.');
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: '#F8FAFC' }}>
      <div className="w-full max-w-md">
        {/* Logo - Now Clickable */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8 hover:opacity-80 transition-opacity">
          <FileText size={40} weight="bold" style={{ color: '#001F3F' }} />
          <span className="text-3xl font-semibold" style={{ fontFamily: 'Outfit', color: '#001F3F' }}>
            ShortlistPro.cv
          </span>
        </Link>

        {/* Card */}
        <div className="card">
          <h1 className="heading-section mb-2">Welcome Back</h1>
          <p className="body-text-sm mb-6">Sign in to continue building your resume</p>

          {error && (
            <div
              data-testid="login-error-message"
              className="mb-4 p-3 border-l-4 rounded-sm"
              style={{ backgroundColor: '#FEF2F2', borderColor: '#EF4444', color: '#991B1B' }}
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label htmlFor="email" className="input-label">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                data-testid="login-email-input"
                className="input-field"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="your@email.com"
              />
            </div>

            <div className="mb-6">
              <label htmlFor="password" className="input-label">
                Password
              </label>
              <input
                id="password"
                type="password"
                data-testid="login-password-input"
                className="input-field"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              data-testid="login-submit-button"
              className="btn-primary w-full"
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t" style={{ borderColor: '#E2E8F0' }}></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2" style={{ backgroundColor: '#FFFFFF', color: '#708090' }}>
                Or continue with
              </span>
            </div>
          </div>

          {/* Google Sign-In Button */}
           <div className="flex justify-center">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              theme="outline"
              size="large"
              text="continue_with"
              shape="rectangular"
              width={googleButtonWidth}
            />
          </div>

          <div className="mt-6 text-center">
            <p className="body-text-sm">
              Don't have an account?{' '}
              <Link
                to="/register"
                data-testid="login-register-link"
                className="font-medium"
                style={{ color: '#50C878' }}
              >
                Create one now
              </Link>
            </p>
          </div>
        </div>

        {/* Back to Home */}
        <div className="mt-6 text-center">
          <Link
            to="/"
            data-testid="login-back-home-link"
            className="inline-flex items-center gap-2 body-text-sm"
            style={{ color: '#708090' }}
          >
            <ArrowLeft size={16} weight="bold" />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
